import React, { useState } from 'react';
import { css } from '@linaria/core';
import type { CalculatedColumn, DragPosition, SortColumn } from './types';
import type { HeaderRowProps } from './HeaderRow';
import SortableHeaderCell from './headerCells/SortableHeaderCell';
import { getCellStyle, getCellClassname } from './utils';

const cellResizable = css`
  &::after {
    content: '';
    cursor: col-resize;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 10px;
  }
`;

const cellResizableClassname = `rdg-cell-resizable ${cellResizable}`;

const colHeaderCell = css`
  cursor: s-resize;
`
const colHeaderCellClassname = `rdg-header-cell ${colHeaderCell}`

const colHeaderStartCell = css`
  cursor: se-resize;
`
const colHeaderStartCellClassname = `rdg-header-start-cell ${colHeaderStartCell}`

type SharedHeaderRowProps<R, SR> = Pick<
  HeaderRowProps<R, SR, React.Key>,
  'onSortColumnsChange' | 'allRowsSelected' | 'onAllRowsSelectionChange' | 'sortColumns'
>;

export interface HeaderCellProps<R, SR> extends SharedHeaderRowProps<R, SR> {
  column: CalculatedColumn<R, SR>;
  colSpan: number | undefined;
  dragPos: React.MutableRefObject<DragPosition<R, SR> | undefined>;
  handleClick: (idx: number) => void;
  isReordering: boolean;
  onAllRowsSelectionChange: (checked: boolean) => void;
  onResize: (column: CalculatedColumn<R, SR>, width: number, mouseDown: boolean) => void;
}

export default function HeaderCell<R, SR>({
  allRowsSelected,
  column,
  colSpan,
  dragPos,
  handleClick,
  isReordering,
  onAllRowsSelectionChange,
  onResize,
  onSortColumnsChange,
  sortColumns,
}: HeaderCellProps<R, SR>) {
  const [resizing, setResizing] = useState(false);

  function onPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (event.pointerType === 'mouse' && event.buttons !== 1) {
      return;
    }

    const { currentTarget, pointerId } = event;
    const { right } = currentTarget.getBoundingClientRect();
    const offset = right - event.clientX;

    if (offset > 11) {
      // +1px to account for the border size
      return;
    }

    function onPointerMove(event: PointerEvent) {
      if (event.pointerId !== pointerId) return;
      if (event.pointerType === 'mouse' && event.buttons !== 1) {
        onPointerUp(event);
        return;
      }
      const width = event.clientX + offset - currentTarget.getBoundingClientRect().left;
      if (width > 0) {
        setResizing(true);
        onResize(column, width, true);
      }
    }

    function onPointerUp(event: PointerEvent) {
      if (event.pointerId !== pointerId) return;
      const width = event.clientX + offset - currentTarget.getBoundingClientRect().left;
      onResize(column, width, false);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    }

    event.preventDefault();
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  }
  const sortIndex = sortColumns?.findIndex((sort) => sort.columnKey === column.key);
  const sortColumn =
    sortIndex !== undefined && sortIndex > -1 ? sortColumns![sortIndex] : undefined;
  const sortDirection = sortColumn?.direction;
  const priority = sortColumn !== undefined && sortColumns!.length > 1 ? sortIndex! + 1 : undefined;
  const ariaSort =
    sortDirection && !priority ? (sortDirection === 'ASC' ? 'ascending' : 'descending') : undefined;

  const onSort = (ctrlClick: boolean) => {
    if (onSortColumnsChange == null) return;
    const { sortDescendingFirst } = column;
    if (sortColumn === undefined) {
      // not currently sorted
      const nextSort: SortColumn = {
        columnKey: column.key,
        direction: sortDescendingFirst ? 'DESC' : 'ASC'
      };
      onSortColumnsChange(sortColumns && ctrlClick ? [...sortColumns, nextSort] : [nextSort]);
    } else {
      let nextSortColumn: SortColumn | undefined;
      if (
        (sortDescendingFirst && sortDirection === 'DESC') ||
        (!sortDescendingFirst && sortDirection === 'ASC')
      ) {
        nextSortColumn = {
          columnKey: column.key,
          direction: sortDirection === 'ASC' ? 'DESC' : 'ASC'
        };
      }
      if (ctrlClick) {
        const nextSortColumns = [...sortColumns!];
        if (nextSortColumn) {
          // swap direction
          nextSortColumns[sortIndex!] = nextSortColumn;
        } else {
          // remove sort
          nextSortColumns.splice(sortIndex!, 1);
        }
        onSortColumnsChange(nextSortColumns);
      } else {
        onSortColumnsChange(nextSortColumn ? [nextSortColumn] : []);
      }
    }
  };

  function getCell() {
    if (column.headerRenderer) {
      return (
        <column.headerRenderer
          column={column}
          sortDirection={sortDirection}
          priority={priority}
          onSort={onSort}
          allRowsSelected={allRowsSelected}
          onAllRowsSelectionChange={onAllRowsSelectionChange}
        />
      );
    }

    if (column.sortable) {
      return (
        <SortableHeaderCell onSort={onSort} sortDirection={sortDirection} priority={priority}>
          {column.name}
        </SortableHeaderCell>
      );
    }

    return column.name;
  }

  function handleHeaderClick() {
    if (!resizing) handleClick(column.idx);
    setResizing(false);
  }

  function handleDragEnter(event: React.MouseEvent<HTMLDivElement>) {
    if (dragPos.current?.column && dragPos.current?.startRowIdx >= 0 && dragPos.current?.startIdx >= 0)
      dragPos.current = { ...dragPos.current, lastRowIdx: -1, lastIdx: column.idx };
  }

  const className = getCellClassname(column, column.headerCellClass, {
    [cellResizableClassname]: column.resizable && !isReordering,
    [colHeaderStartCellClassname]: column.idx === 0 && !isReordering,
    [colHeaderCellClassname]: column.idx !== 0 && !isReordering
  });

  return (
    <div
      aria-colindex={column.idx + 1}
      aria-sort={ariaSort}
      aria-colspan={colSpan}
      className={className}
      onClick={handleHeaderClick}
      onMouseEnter={handleDragEnter}
      onPointerDown={column.resizable ? onPointerDown : undefined}
      role="columnheader"
      style={{ ...getCellStyle(column, colSpan) }}
    >
      {getCell()}
    </div>
  );
}
